import clonedeep from 'lodash.clonedeep';
import sentry from '@/analytics/sentry';

import {
    getBrandLogos,
    getBrandPalette,
    getTemplateDependencyList,
    createLogoDependency,
    deleteLogoDependency
} from '@/api/campaigns/campaignUnlayerEmail';

import getUnusedLogoDependencies from './getUnusedLogoDependencies';

import {
    cleanDependenciesFromDesign,
    updateLogoDependenciesForDuplicatedRow,
    updateLogoDependenciesForDuplicatedContent,
} from '@/components/unlayer/unlayer.util';

export default function brandingEventHandler({
    templateId,
    appId,
    environment,
    design,
}) {
    let reloadDesignHandler = null;

    let brandLogos = [];
    let brandPalette = [];
    let logoDependencies = [];
    let initialLoadComplete = false;

    const runInitialCleanup = async () => {
        if (!reloadDesignHandler || !initialLoadComplete) {
            return;
        }

        if (!design?.value?.body?.rows) {
            return;
        }

        const designUpdated = cleanDependenciesFromDesign(templateId, {
            design: design.value,
            brandLogos,
            logoDependencies,
        });

        if (designUpdated) {
            await fetchLogoDependencies();
            await reloadDesignHandler(design.value);
        }
    };

    const brandingCenterSettingsURL = (() => {
        const path = `/settings/branding-center/?app_id=${appId}`;

        switch (environment.toLowerCase()) {
        case 'production':
        default:
            return `https://keap.app${path}`;
        case 'development':
        case 'sandbox':
            return `http://localhost:10239${path}`;
        case 'integration':
            return `https://integration.keap.app${path}`;
        case 'proofing':
        case 'staging':
            return `https://staging.keap.app${path}`;
        }
    })();

    const fetchBrandLogos = async () => {
        const { logos } = await getBrandLogos();

        brandLogos = logos;
    };

    const fetchBrandPalette = async () => {
        const { palette } = await getBrandPalette();

        brandPalette = palette;
    };

    const fetchLogoDependencies = async () => {
        const { templateDependencies } = await getTemplateDependencyList(templateId);

        logoDependencies = templateDependencies;
    };

    Promise.all([
        fetchBrandLogos(),
        fetchBrandPalette(),
        fetchLogoDependencies(),
    ]).then(async () => {
        initialLoadComplete = true;
        await runInitialCleanup();
    }).catch(sentry.captureException);

    const deleteUnusedLogoDependencies = async (_design) => {
        const depsToDelete = getUnusedLogoDependencies({
            design: _design,
            logoDependencies,
            templateId,
        });

        try {
            await Promise.all(depsToDelete.map((key) => deleteLogoDependency(templateId, key)));
        } catch (e) {
            sentry.captureException(e);
        }
    }

    const designUpdatedEventHandler = async ({ data, change }) => {
        switch (change.type) {
        case 'row:removed': {
            deleteUnusedLogoDependencies(data.design);
            break;
        }

        case 'content:removed': {
            const dependencyKey = change.item.values?.logoTool?.dependencyKey;

            if (dependencyKey) deleteLogoDependency(templateId, dependencyKey);
            break;
        }

        case 'content:added':

        // eslint-disable-next-line no-fallthrough
        case 'row:added': {
            const targetId = change?.item?.id;

            if (targetId) {
                const fn = change?.type === 'row:added' ? updateLogoDependenciesForDuplicatedRow : updateLogoDependenciesForDuplicatedContent;

                try {
                    const resultOrPromise = fn(
                        data.design,
                        targetId,
                        {
                            brandLogos,
                            logoDependencies,
                            templateId,
                        }
                    );

                    if (resultOrPromise !== false) {
                        await resultOrPromise;

                        reloadDesignHandler(data.design);
                    }
                } catch (e) {
                    sentry.captureException(e);
                }
            }

            break;
        }

        default:
            break;
        }
    };

    const iframeEventHandler = async ({ data, ports }) => {
        const {
            keapRequestType,
            requestData,
        } = data;

        if (!keapRequestType) return false;

        const sendPort = ports[0];
        let resp;

        try {
            switch (keapRequestType) {
            case 'showBrandingServiceCenter':
                window.open(brandingCenterSettingsURL, '_blank');
                break;

            case 'getBrandLogos':
                if (!requestData?.useCache) {
                    await fetchBrandLogos();
                }

                if (requestData?.reloadDesign) {
                    const designUpdated = cleanDependenciesFromDesign(templateId, {
                        design: design.value,
                        brandLogos,
                        logoDependencies,
                    });

                    if (designUpdated) {
                        await fetchLogoDependencies();

                        await reloadDesignHandler();
                    }
                }

                resp = brandLogos;
                break;

            case 'getBrandLogoDependencies':
                if (!requestData?.useCache) {
                    await fetchLogoDependencies();
                }

                resp = logoDependencies;
                break;

            case 'getBrandColors':
                await fetchBrandPalette();

                resp = brandPalette;
                break;

            case 'createLogoDependency': {
                const { logoId, isPrimary } = requestData;
                const newDependency = await createLogoDependency(templateId, logoId, isPrimary);

                logoDependencies.push(newDependency);

                resp = newDependency;
                break;
            }

            case 'deleteLogoDependency': {
                const { dependencyKey } = requestData;

                await deleteLogoDependency(templateId, dependencyKey);
                const filteredDependencies = logoDependencies.filter((dependency) => dependency.dependencyKey !== dependencyKey);

                logoDependencies = [...filteredDependencies];
                break;
            }

            default:
                return false;
            }

            sendPort.postMessage({ success: true, response: clonedeep(resp) });
        } catch (e) {
            sentry.captureException(e);
            sendPort.postMessage({ success: false, error: `${e}` });
        }

        return true;
    };

    return {
        iframeEventHandler,
        designUpdatedEventHandler,
        registerReloadDesignCallback: (val) => {
            reloadDesignHandler = val;
            runInitialCleanup();
        },
    };
}
